import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { GraduationCap, UserCircle, Key, X, BellRinging, CaretRight, CaretLeft, Phone, UploadSimple } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const TK_GROUPS = [
  { group: "TK B", subs: ["TK B-1", "TK B-2", "TK B-3"] },
  { group: "TK A", subs: ["TK A-1", "TK A-2", "TK A-3"] },
  { group: "KB", subs: ["KB"] },
];
const SD_GROUPS = Array.from({ length: 6 }, (_, i) => ({ group: `Kelas ${i + 1}`, subs: [`Kelas ${i + 1}A`, `Kelas ${i + 1}B`] }));

export default function ManageUsers() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const GROUPS = user?.school === "sdqu" ? SD_GROUPS : TK_GROUPS;
  const [tab, setTab] = useState("guru");
  const [users, setUsers] = useState([]);
  const [parents, setParents] = useState([]);
  const [requests, setRequests] = useState([]);
  const [target, setTarget] = useState(null); // user to reset
  const [pw, setPw] = useState("");
  const [pGroup, setPGroup] = useState(null);
  const [pSub, setPSub] = useState(null);
  const [confirmReset, setConfirmReset] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);
  const [resetResult, setResetResult] = useState(null);

  const loadGuru = () => api.get("/users?role=guru").then((r) => setUsers(r.data));
  const loadParents = () => api.get("/admin/parents").then((r) => setParents(r.data));
  const loadRequests = () => api.get("/admin/reset-requests").then((r) => setRequests(r.data));

  useEffect(() => {
    if (tab === "requests") loadRequests();
    else if (tab === "orangtua") { loadParents(); setPGroup(null); setPSub(null); }
    else loadGuru();
  }, [tab]);
  useEffect(() => { loadRequests(); }, []);

  const doReset = async () => {
    if (pw.length < 6) { toast.error("Kata sandi minimal 6 karakter"); return; }
    try {
      await api.post(`/admin/users/${target.id}/reset-password`, { password: pw });
      toast.success(`Password ${target.name} berhasil direset`);
      setTarget(null); setPw(""); loadRequests();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const dismiss = async (id) => { await api.delete(`/admin/reset-requests/${id}`); toast.success("Permintaan ditutup"); loadRequests(); };
  const doBulkReset = async () => {
    setResetBusy(true);
    try {
      const { data } = await api.post("/admin/reset-parent-logins");
      setConfirmReset(false);
      setResetResult(data);
      loadParents();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setResetBusy(false);
  };

  const TABS = [["guru", "Guru"], ["orangtua", "Orang Tua"], ["requests", `Permintaan${requests.length ? ` (${requests.length})` : ""}`]];

  const countIn = (sub) => parents.filter((p) => p.kelas === sub).length;
  const subParents = parents.filter((p) => p.kelas === pSub);

  return (
    <div>
      <PageHeader title="Kelola Pengguna" />
      <div className="px-5 pt-4">
        <div className="flex gap-2 mb-4">
          {TABS.map(([v, l]) => (
            <button key={v} data-testid={`users-tab-${v}`} onClick={() => setTab(v)}
              className={`flex-1 py-2.5 rounded-full text-xs font-semibold transition-colors ${tab === v ? "bg-primary text-white" : "bg-white border border-stone-200 text-stone-500"}`}>
              {l}
            </button>
          ))}
        </div>

        {(tab === "guru" || tab === "orangtua") && (
          <button data-testid="import-users-btn" onClick={() => navigate(`/kelola/import/${tab}`)}
            className="w-full mb-4 border-2 border-emerald-500 text-emerald-600 font-semibold py-2.5 rounded-2xl flex items-center justify-center gap-2 active:scale-95 transition-transform">
            <UploadSimple size={18} weight="bold" /> Import {tab === "guru" ? "Guru" : "Orang Tua"} dari Excel
          </button>
        )}

        {tab === "orangtua" && (
          <button data-testid="reset-parent-logins-btn" onClick={() => setConfirmReset(true)}
            className="w-full mb-4 border-2 border-amber-500 text-amber-600 font-semibold py-2.5 rounded-2xl flex items-center justify-center gap-2 active:scale-95 transition-transform">
            <Key size={18} weight="bold" /> Reset Login Orang Tua (Nomor Siswa · 123456)
          </button>
        )}

        {tab === "requests" && (
          <div className="space-y-2">
            {requests.map((r) => (
              <motion.div key={r.id} data-testid={`req-${r.id}`} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-2xl border border-amber-200 p-3.5 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                    <BellRinging size={20} weight="fill" className="text-amber-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-stone-800 text-sm">{r.name}</p>
                    <p className="text-stone-400 text-xs truncate">{r.email || r.phone} • {r.role === "guru" ? "Guru" : "Orang Tua"}</p>
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <button data-testid={`req-reset-${r.id}`} onClick={() => setTarget({ id: r.user_id, name: r.name })}
                    className="flex-1 bg-primary text-white text-xs font-semibold py-2 rounded-full active:scale-95 transition-transform">Reset Password</button>
                  <button data-testid={`req-dismiss-${r.id}`} onClick={() => dismiss(r.id)}
                    className="px-4 bg-stone-100 text-stone-500 text-xs font-semibold py-2 rounded-full active:scale-95 transition-transform">Tutup</button>
                </div>
              </motion.div>
            ))}
            {requests.length === 0 && <p className="text-center text-stone-400 mt-8">Tidak ada permintaan reset.</p>}
          </div>
        )}

        {tab === "guru" && (
          <div className="space-y-2">
            {users.map((u, i) => (
              <motion.div key={u.id} data-testid={`user-${u.id}`}
                initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.03 }}
                className="bg-white rounded-2xl border border-stone-200/60 p-3.5 flex items-center gap-3 shadow-sm">
                <div className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center">
                  <GraduationCap size={22} weight="duotone" className="text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-stone-800 text-sm">{u.name}</p>
                  <p className="text-stone-400 text-xs truncate">{u.phone || u.email}{u.kelas ? ` • ${u.kelas}` : ""}</p>
                </div>
                <button data-testid={`reset-user-${u.id}`} onClick={() => setTarget(u)}
                  className="w-9 h-9 rounded-full bg-stone-100 flex items-center justify-center text-stone-500 hover:text-primary active:scale-90 transition-transform" aria-label="Reset password">
                  <Key size={18} weight="duotone" />
                </button>
              </motion.div>
            ))}
            {users.length === 0 && <p className="text-center text-stone-400 mt-8">Belum ada data.</p>}
          </div>
        )}

        {tab === "orangtua" && (
          <div className="space-y-2.5">
            {/* Level 1: groups */}
            {!pGroup && GROUPS.map((g, i) => {
              const total = parents.filter((p) => (p.kelas || "").startsWith(g.group === "KB" ? "KB" : g.group)).length;
              return (
                <motion.button key={g.group} data-testid={`parent-group-${g.group}`} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                  onClick={() => setPGroup(g.group)}
                  className="w-full bg-white rounded-2xl border border-stone-200/60 p-4 flex items-center justify-between shadow-sm active:scale-[0.98] transition-transform">
                  <span className="font-heading font-semibold text-stone-800">{g.group}</span>
                  <span className="flex items-center gap-2">
                    <span className="text-sm font-bold text-primary">{total} <span className="text-stone-400 font-medium">orang tua</span></span>
                    <CaretRight size={18} className="text-stone-300" />
                  </span>
                </motion.button>
              );
            })}

            {/* Level 2: sub-classes */}
            {pGroup && !pSub && (
              <>
                <button data-testid="parent-back-group" onClick={() => setPGroup(null)}
                  className="flex items-center gap-1 text-stone-500 text-sm font-semibold mb-1">
                  <CaretLeft size={16} weight="bold" /> {pGroup}
                </button>
                {GROUPS.find((g) => g.group === pGroup).subs.map((sub, i) => (
                  <motion.button key={sub} data-testid={`parent-sub-${sub}`} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                    onClick={() => setPSub(sub)}
                    className="w-full bg-white rounded-2xl border border-stone-200/60 p-4 flex items-center justify-between shadow-sm active:scale-[0.98] transition-transform">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-2xl bg-primary/10 flex items-center justify-center">
                        <span className="font-heading font-bold text-primary text-sm">{sub.split("-")[1] || sub}</span>
                      </div>
                      <span className="font-heading font-semibold text-stone-800">{sub}</span>
                    </div>
                    <span className="flex items-center gap-2">
                      <span className="text-sm font-bold text-primary">{countIn(sub)}</span>
                      <CaretRight size={18} className="text-stone-300" />
                    </span>
                  </motion.button>
                ))}
              </>
            )}

            {/* Level 3: parents in sub-class */}
            {pSub && (
              <>
                <button data-testid="parent-back-sub" onClick={() => setPSub(null)}
                  className="flex items-center gap-1 text-stone-500 text-sm font-semibold mb-1">
                  <CaretLeft size={16} weight="bold" /> {pSub}
                </button>
                {subParents.map((p, i) => (
                  <motion.div key={p.student_id} data-testid={`parent-${p.student_id}`}
                    initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.03 }}
                    className="bg-white rounded-2xl border border-stone-200/60 p-3.5 flex items-center gap-3 shadow-sm">
                    <div className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                      <UserCircle size={22} weight="duotone" className="text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-stone-800 text-sm truncate">Orang Tua {p.student_name}</p>
                      <p className="text-stone-400 text-xs flex items-center gap-1 truncate">
                        <Phone size={12} weight="fill" /> {p.phone || "—"}
                      </p>
                    </div>
                    {p.parent_id && (
                      <button data-testid={`reset-parent-${p.parent_id}`} onClick={() => setTarget({ id: p.parent_id, name: `Orang Tua ${p.student_name}` })}
                        className="w-9 h-9 rounded-full bg-stone-100 flex items-center justify-center text-stone-500 hover:text-primary active:scale-90 transition-transform" aria-label="Reset password">
                        <Key size={18} weight="duotone" />
                      </button>
                    )}
                  </motion.div>
                ))}
                {subParents.length === 0 && <p className="text-center text-stone-400 mt-8">Belum ada orang tua di kelas ini.</p>}
              </>
            )}
          </div>
        )}
      </div>

      {target && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-end md:items-center justify-center" onClick={() => { setTarget(null); setPw(""); }}>
          <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} onClick={(e) => e.stopPropagation()}
            className="bg-white w-full md:max-w-md rounded-t-3xl md:rounded-3xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-lg text-stone-800">Reset Password</h3>
              <button onClick={() => { setTarget(null); setPw(""); }}><X size={22} className="text-stone-400" /></button>
            </div>
            <p className="text-sm text-stone-500 mb-3">Buat kata sandi baru untuk <span className="font-semibold text-stone-700">{target.name}</span></p>
            <input data-testid="reset-modal-input" type="password" value={pw} onChange={(e) => setPw(e.target.value)}
              placeholder="Kata sandi baru (min. 6 karakter)"
              className="w-full bg-stone-50 border border-stone-200 rounded-xl py-3 px-3 mb-4 outline-none focus:border-primary" />
            <button data-testid="reset-modal-submit" onClick={doReset}
              className="w-full bg-primary text-white font-semibold py-3.5 rounded-full active:scale-95 transition-transform">Simpan Password Baru</button>
          </motion.div>
        </div>
      )}

      {confirmReset && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-end md:items-center justify-center" onClick={() => !resetBusy && setConfirmReset(false)}>
          <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} onClick={(e) => e.stopPropagation()}
            className="bg-white w-full md:max-w-md rounded-t-3xl md:rounded-3xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-lg text-stone-800">Reset Login Orang Tua</h3>
              <button onClick={() => !resetBusy && setConfirmReset(false)}><X size={22} className="text-stone-400" /></button>
            </div>
            <p className="text-sm text-stone-600 mb-4">
              Semua akun <span className="font-semibold">Orang Tua</span> (TK & SDQu) akan diubah:
              <br />• Username → <span className="font-semibold">Nomor Siswa</span> anak
              <br />• Kata sandi → <span className="font-semibold">123456</span>
              <br /><br />Akun tanpa Nomor Siswa atau dengan Nomor Siswa duplikat akan <span className="font-semibold">dilewati</span>. Lanjutkan?
            </p>
            <div className="flex gap-2">
              <button onClick={() => setConfirmReset(false)} disabled={resetBusy}
                className="flex-1 bg-stone-100 text-stone-500 font-semibold py-3 rounded-full active:scale-95 transition-transform disabled:opacity-60">Batal</button>
              <button data-testid="confirm-reset-submit" onClick={doBulkReset} disabled={resetBusy}
                className="flex-1 bg-amber-500 text-white font-semibold py-3 rounded-full active:scale-95 transition-transform disabled:opacity-60">{resetBusy ? "Memproses…" : "Ya, Reset"}</button>
            </div>
          </motion.div>
        </div>
      )}

      {resetResult && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-end md:items-center justify-center" onClick={() => setResetResult(null)}>
          <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} onClick={(e) => e.stopPropagation()}
            className="bg-white w-full md:max-w-md rounded-t-3xl md:rounded-3xl p-5" data-testid="reset-result-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-lg text-stone-800">Reset Selesai</h3>
              <button onClick={() => setResetResult(null)}><X size={22} className="text-stone-400" /></button>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between bg-stone-50 rounded-xl px-3 py-2"><span className="text-stone-500">Total akun orang tua</span><span className="font-bold text-stone-800">{resetResult.total_parents}</span></div>
              <div className="flex justify-between bg-emerald-50 rounded-xl px-3 py-2"><span className="text-emerald-600">Berhasil direset</span><span className="font-bold text-emerald-700">{resetResult.updated}</span></div>
              <div className="flex justify-between bg-amber-50 rounded-xl px-3 py-2"><span className="text-amber-600">Dilewati (tanpa Nomor Siswa)</span><span className="font-bold text-amber-700">{resetResult.skipped_no_number}</span></div>
              <div className="flex justify-between bg-orange-50 rounded-xl px-3 py-2"><span className="text-orange-600">Dilewati (Nomor Siswa duplikat)</span><span className="font-bold text-orange-700">{resetResult.skipped_duplicate}</span></div>
            </div>
            {resetResult.skipped_no_number_details && resetResult.skipped_no_number_details.length > 0 && (
              <div className="mt-4" data-testid="skipped-no-number-list">
                <p className="text-xs font-semibold text-amber-700 mb-1.5">Anak tanpa Nomor Siswa ({resetResult.skipped_no_number_details.length}) — isi Nomor Siswa lalu reset ulang</p>
                <div className="max-h-44 overflow-y-auto rounded-xl border border-amber-200 divide-y divide-amber-100 bg-amber-50/40">
                  {resetResult.skipped_no_number_details.map((d, i) => (
                    <div key={i} data-testid={`skipped-row-${i}`} className="px-3 py-2 text-xs flex items-center justify-between gap-2">
                      <span className="font-semibold text-stone-700 truncate flex-1">{d.child_name}</span>
                      <span className="text-stone-400 shrink-0">{d.kelas}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {resetResult.details && resetResult.details.length > 0 && (
              <div className="mt-4">
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-xs font-semibold text-stone-500">Login Baru Orang Tua ({resetResult.details.length})</p>
                  <button data-testid="copy-creds-btn"
                    onClick={() => { navigator.clipboard?.writeText(resetResult.details.map((d) => `${d.name}\t${d.username}\t${d.password}`).join("\n")); toast.success("Daftar login disalin"); }}
                    className="text-primary text-xs font-semibold">Salin Semua</button>
                </div>
                <div className="max-h-56 overflow-y-auto rounded-xl border border-stone-200 divide-y divide-stone-100">
                  {resetResult.details.map((d, i) => (
                    <div key={i} data-testid={`cred-row-${i}`} className="px-3 py-2 text-xs flex items-center justify-between gap-2">
                      <span className="font-semibold text-stone-700 truncate flex-1">{d.name}</span>
                      <span className="text-stone-500 shrink-0">User: <b className="text-stone-700">{d.username}</b> · Pass: <b className="text-stone-700">{d.password}</b></span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <button onClick={() => setResetResult(null)}
              className="w-full mt-4 bg-primary text-white font-semibold py-3.5 rounded-full active:scale-95 transition-transform">Selesai</button>
          </motion.div>
        </div>
      )}
    </div>
  );
}
